import React, { useState, useRef, useEffect } from 'react';
import { Modal } from '../../../shared/components/Modal';
import { Button } from '../../../shared/components/Button';
import { OCRBlock, NormalizedOCRDocument } from '../../../core/ocr/ocrTypes';
import { executeOCR } from '../../../core/ocr/ocrAdapter';
import { convertOCRBlocksToDocumentBlocks } from '../../../core/ocr/ocrToAstConverter';
import { DocumentSection, DocumentBlock } from '../../../core/ast/document.types';
import { useToast } from '../../../shared/context/ToastContext';
import {
  Scan,
  Trash2,
  ArrowUp,
  ArrowDown,
  Clock,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  FolderDown,
} from 'lucide-react';
import katex from 'katex';

interface OCRScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  sections: DocumentSection[];
  defaultSectionId?: string | null;
  onInsertBlocks: (sectionId: string, blocks: DocumentBlock[]) => void;
}

export const OCRScannerModal: React.FC<OCRScannerModalProps> = ({
  isOpen,
  onClose,
  sections,
  defaultSectionId,
  onInsertBlocks,
}) => {
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Workflow state: 'upload' | 'scanning' | 'review'
  const [step, setStep] = useState<'upload' | 'scanning' | 'review'>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [scanProgress, setScanProgress] = useState<string>('');
  const [normalizedDoc, setNormalizedDoc] = useState<NormalizedOCRDocument | null>(null);
  const [editableBlocks, setEditableBlocks] = useState<OCRBlock[]>([]);
  const [targetSectionId, setTargetSectionId] = useState<string>(
    defaultSectionId || sections[0]?.id || ''
  );
  const [keepOriginalImage, setKeepOriginalImage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (defaultSectionId) {
      setTargetSectionId(defaultSectionId);
    } else if (sections.length > 0 && !targetSectionId) {
      setTargetSectionId(sections[0].id);
    }
  }, [defaultSectionId, sections]);

  // Clipboard paste event listener for screenshots (Ctrl+V)
  useEffect(() => {
    if (!isOpen || step !== 'upload') return;

    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          const file = items[i].getAsFile();
          if (file) {
            handleFileSelected(file);
            showToast('Pasted image from clipboard.', 'info');
            break;
          }
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [isOpen, step]);

  const handleFileSelected = (file: File) => {
    if (!file.type.startsWith('image/')) {
      showToast('Please select a valid image file (PNG, JPG, WEBP).', 'error');
      return;
    }
    setSelectedFile(file);
    setErrorMessage(null);

    const reader = new FileReader();
    reader.onload = () => setPreviewUrl(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleStartOCR = async () => {
    if (!selectedFile && !previewUrl) return;

    setStep('scanning');
    setErrorMessage(null);
    setScanProgress('Initializing OCR service...');

    abortControllerRef.current = new AbortController();

    try {
      const result = await executeOCR(selectedFile || previewUrl!, {
        fileName: selectedFile?.name || 'clipboard-image.png',
        fileSize: selectedFile?.size || 0,
        onProgress: (status) => setScanProgress(status),
        signal: abortControllerRef.current.signal,
      });

      setNormalizedDoc(result);
      setEditableBlocks(result.blocks);
      setStep('review');
      showToast(`Recognized ${result.blocks.length} structured content blocks.`, 'success');
    } catch (err: any) {
      console.error('OCR Error:', err);
      setErrorMessage(err.message || 'OCR processing failed. Please check network or try a clearer image.');
      setStep('upload');
      showToast('OCR scanning failed.', 'error');
    }
  };

  const handleCancelScan = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setStep('upload');
    setScanProgress('');
  };

  const handleUpdateBlockContent = (index: number, updates: Partial<OCRBlock>) => {
    setEditableBlocks((prev) =>
      prev.map((blk, i) => (i === index ? ({ ...blk, ...updates } as OCRBlock) : blk))
    );
  };

  const handleRemoveBlock = (index: number) => {
    setEditableBlocks((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMoveBlock = (index: number, direction: 'up' | 'down') => {
    const targetIdx = direction === 'up' ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= editableBlocks.length) return;

    setEditableBlocks((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(index, 1);
      updated.splice(targetIdx, 0, moved);
      return updated;
    });
  };

  const handleConfirmInsert = async () => {
    if (!targetSectionId) {
      showToast('Please select a target section to insert into.', 'error');
      return;
    }

    const blocksToInsert: OCRBlock[] = [...editableBlocks];

    if (keepOriginalImage && previewUrl) {
      blocksToInsert.unshift({
        id: `ocr-orig-img-${Date.now()}`,
        type: 'image',
        dataUrl: previewUrl,
        caption: `Source Image: ${selectedFile?.name || 'Scanned Lab Notes'}`,
      });
    }

    const documentBlocks = convertOCRBlocksToDocumentBlocks(blocksToInsert);
    onInsertBlocks(targetSectionId, documentBlocks);
    showToast(`Inserted ${documentBlocks.length} blocks into document.`, 'success');
    handleResetAndClose();
  };

  const handleResetAndClose = () => {
    setStep('upload');
    setSelectedFile(null);
    setPreviewUrl(null);
    setNormalizedDoc(null);
    setEditableBlocks([]);
    setErrorMessage(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleResetAndClose}
      title="Smart OCR & Content Recognition"
      maxWidth="780px"
      footer={
        step === 'review' ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Button variant="ghost" onClick={() => setStep('upload')}>
              Back to Image
            </Button>
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <Button variant="ghost" onClick={handleResetAndClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                icon={<CheckCircle2 size={14} />}
                onClick={handleConfirmInsert}
                disabled={editableBlocks.length === 0}
              >
                Insert {editableBlocks.length} Blocks into Report
              </Button>
            </div>
          </div>
        ) : undefined
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {/* Error Notification Banner */}
        {errorMessage && (
          <div
            style={{
              padding: 'var(--space-3)',
              backgroundColor: 'var(--color-danger-subtle)',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-2)',
              color: 'var(--color-danger)',
              fontSize: 'var(--font-size-xs)',
            }}
          >
            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <strong>OCR Recognition Issue:</strong> {errorMessage}
            </div>
          </div>
        )}

        {/* STEP 1: Upload / Paste Image */}
        {step === 'upload' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)', margin: 0 }}>
              Upload handwritten lab calculations, whiteboard notes, circuit diagrams, or textbook tables. Our OCR engine intelligently isolates mathematical formulas, data tables, and formatted text.
            </p>

            <input
              type="file"
              ref={fileInputRef}
              accept="image/png,image/jpeg,image/webp"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileSelected(file);
              }}
            />

            {!previewUrl ? (
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files?.[0];
                  if (file) handleFileSelected(file);
                }}
                style={{
                  border: '2px dashed var(--border-default)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-8)',
                  textAlign: 'center',
                  backgroundColor: 'var(--bg-surface-subtle)',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: 'var(--primary-50)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--primary-600)',
                  }}
                >
                  <Scan size={24} />
                </div>
                <div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: 'var(--font-size-sm)' }}>
                    Drop lab image here or click to browse
                  </h4>
                  <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                    Supports PNG, JPG, WEBP • Or press <strong>Ctrl+V</strong> to paste from clipboard
                  </p>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'var(--bg-surface-subtle)',
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-subtle)',
                    maxHeight: '260px',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={previewUrl}
                    alt="OCR Preview"
                    style={{ maxHeight: '240px', maxWidth: '100%', objectFit: 'contain' }}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    {selectedFile?.name || 'Clipboard Image'} {selectedFile ? `(${(selectedFile.size / 1024).toFixed(1)} KB)` : ''}
                  </span>

                  <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
                      Change Image
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      icon={<Sparkles size={13} />}
                      onClick={handleStartOCR}
                    >
                      Recognize & Extract Content
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 2: Scanning / Active Processing State */}
        {step === 'scanning' && (
          <div
            style={{
              padding: 'var(--space-10)',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--space-4)',
            }}
          >
            <div
              style={{
                width: 54,
                height: 54,
                borderRadius: '50%',
                backgroundColor: 'var(--primary-50)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--primary-600)',
              }}
            >
              <Clock size={28} className="animate-spin" />
            </div>
            <div>
              <h3 style={{ margin: '0 0 6px 0', fontSize: 'var(--font-size-md)' }}>
                Analyzing Document with OCR Engine
              </h3>
              <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)' }}>
                {scanProgress || 'Classifying mathematical formulas, tables, and procedural notes...'}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={handleCancelScan}>
              Cancel Scan
            </Button>
          </div>
        )}

        {/* STEP 3: Review & Edit Extracted Blocks */}
        {step === 'review' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {/* Insertion Target & Options Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 'var(--space-2) var(--space-3)',
                backgroundColor: 'var(--bg-surface-subtle)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border-subtle)',
                flexWrap: 'wrap',
                gap: 'var(--space-2)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <FolderDown size={14} color="var(--primary-600)" />
                <label style={{ fontSize: '11px', fontWeight: 600 }}>Insert Into Section:</label>
                {normalizedDoc?.metadata.durationMs && (
                  <span className="badge badge-neutral" style={{ fontSize: '10px' }}>
                    {(normalizedDoc.metadata.durationMs / 1000).toFixed(1)}s
                  </span>
                )}
                <select
                  className="form-select text-xs"
                  style={{ width: '220px', height: '28px', padding: '2px 6px' }}
                  value={targetSectionId}
                  onChange={(e) => setTargetSectionId(e.target.value)}
                >
                  {sections.map((sec, idx) => (
                    <option key={sec.id} value={sec.id}>
                      #{idx + 1} {sec.title}
                    </option>
                  ))}
                </select>
              </div>

              <label style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={keepOriginalImage}
                  onChange={(e) => setKeepOriginalImage(e.target.checked)}
                />
                <span>Also attach original source image</span>
              </label>
            </div>

            {/* Extracted Blocks List */}
            <div
              style={{
                maxHeight: '380px',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-3)',
                paddingRight: '4px',
              }}
            >
              {editableBlocks.map((block, idx) => (
                <div
                  key={block.id}
                  style={{
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-sm)',
                    padding: 'var(--space-3)',
                    backgroundColor: 'var(--bg-surface)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-2)',
                  }}
                >
                  {/* Block Header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border-subtle)', paddingBottom: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {block.type === 'heading' && <span className="badge badge-primary" style={{ fontSize: '9px' }}>HEADING</span>}
                      {block.type === 'text' && <span className="badge badge-neutral" style={{ fontSize: '9px' }}>PARAGRAPH</span>}
                      {block.type === 'formula' && <span className="badge badge-success" style={{ fontSize: '9px' }}>LATEX FORMULA</span>}
                      {block.type === 'table' && <span className="badge badge-warning" style={{ fontSize: '9px' }}>DATA TABLE</span>}
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Block #{idx + 1}</span>
                    </div>

                    <div style={{ display: 'flex', gap: 2 }}>
                      <button
                        type="button"
                        className="icon-btn"
                        style={{ width: 20, height: 20 }}
                        disabled={idx === 0}
                        onClick={() => handleMoveBlock(idx, 'up')}
                        title="Move Up"
                      >
                        <ArrowUp size={10} />
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        style={{ width: 20, height: 20 }}
                        disabled={idx === editableBlocks.length - 1}
                        onClick={() => handleMoveBlock(idx, 'down')}
                        title="Move Down"
                      >
                        <ArrowDown size={10} />
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        style={{ width: 20, height: 20 }}
                        onClick={() => handleRemoveBlock(idx)}
                        title="Delete Block"
                      >
                        <Trash2 size={10} color="var(--color-danger)" />
                      </button>
                    </div>
                  </div>

                  {/* Block-specific Editable Content */}
                  {(block.type === 'text' || block.type === 'heading') && (
                    <textarea
                      className="form-textarea text-xs"
                      rows={block.type === 'heading' ? 1 : 2}
                      value={block.content}
                      onChange={(e) => handleUpdateBlockContent(idx, { content: e.target.value })}
                      style={{ fontWeight: block.type === 'heading' ? 700 : 'normal' }}
                    />
                  )}

                  {block.type === 'formula' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                      <input
                        type="text"
                        className="form-input font-mono text-xs"
                        value={block.latex}
                        onChange={(e) => handleUpdateBlockContent(idx, { latex: e.target.value })}
                      />
                      {/* Live Mini Preview */}
                      <div
                        style={{
                          padding: '6px',
                          backgroundColor: 'var(--bg-surface-subtle)',
                          borderRadius: 'var(--radius-xs)',
                          textAlign: 'center',
                          overflowX: 'auto',
                        }}
                        dangerouslySetInnerHTML={{
                          __html: (() => {
                            try {
                              return katex.renderToString(block.latex || 'x=0', { displayMode: true, throwOnError: false });
                            } catch {
                              return '<span>[Invalid LaTeX]</span>';
                            }
                          })(),
                        }}
                      />
                    </div>
                  )}

                  {block.type === 'table' && (
                    <div style={{ overflowX: 'auto', maxHeight: '160px' }}>
                      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '11px' }}>
                        <thead>
                          <tr>
                            {block.headers.map((h, cIdx) => (
                              <th key={cIdx} style={{ padding: '4px', border: '1px solid var(--border-subtle)', backgroundColor: 'var(--bg-surface-subtle)' }}>
                                <input
                                  type="text"
                                  className="form-input text-xs"
                                  style={{ padding: '2px 4px', fontWeight: 700 }}
                                  value={h}
                                  onChange={(e) => {
                                    const newHeaders = [...block.headers];
                                    newHeaders[cIdx] = e.target.value;
                                    handleUpdateBlockContent(idx, { headers: newHeaders });
                                  }}
                                />
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {block.rows.map((row, rIdx) => (
                            <tr key={rIdx}>
                              {row.map((cell, cIdx) => (
                                <td key={cIdx} style={{ padding: '2px', border: '1px solid var(--border-subtle)' }}>
                                  <input
                                    type="text"
                                    className="form-input text-xs"
                                    style={{ padding: '2px 4px' }}
                                    value={cell}
                                    onChange={(e) => {
                                      const newRows = block.rows.map((r, rI) =>
                                        rI === rIdx ? r.map((c, cI) => (cI === cIdx ? e.target.value : c)) : r
                                      );
                                      handleUpdateBlockContent(idx, { rows: newRows });
                                    }}
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}

              {editableBlocks.length === 0 && (
                <div style={{ padding: 'var(--space-6)', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
                  No content detected. Click "Back to Image" to try another image.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
